/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, NgZone} from '@angular/core';
import * as _ from 'lodash';
import {AppEventBusService} from '../common/events/app-event-bus.service';

import template from './print-line-item-edit-page.component.html';
import {AdminResourceEditPageBase} from '../common/controller/admin-resource-edit-page-base';
import {AngularModalService} from '../common/modal/angular-modal.service';
import {GetCampaignQrCodeModalComponent} from './get-campaign-qr-code-modal.component';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {buildElectionFormOptions, buildElectionSelectGroups, sameElectionId} from '../../components/util/election-form-options';
import {
  advertiserNeedsPoliticalData,
  buildTopicsFromPoliticalBudgets,
  linkedPoliticalCampaignRefs,
  loadPoliticalCampaignTopicsByElection
} from '../../components/util/political-campaign-advertiser-topics';
import {AdvertiserResource} from '../../components/api/resources/resource/advertiser.resource';
import {PrintLineItemResource} from '../../components/api/resources/resource/print-line-item.resource';
import {PublicPoliticalCampaignResource} from '../../components/api/resources/resource/political-campaign-public.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {compareById} from '../common/util/object-id-comparison';

@Component({
  standalone: false,
  selector: 'adn-print-line-item-edit-page',
  template
})
export class PrintLineItemEditPageComponent extends AdminResourceEditPageBase {
  showAdvertiserSearch: boolean;
  elections: any[] = [];
  electionSelectGroups: any = {dated: [], undated: [], useGroups: false, groups: []};
  advertisers: any[] = [];
  advertiserBudgets: any = null;
  advertiserBudgetsResolved = true;

  readonly selectObj = {
    onSelect: () => {
      if (!this.advertiserBudgets) {
        this.loadUpAdvertiser();
      }
    }
  };

  constructor(
    eventBus: AppEventBusService,
    @Inject(UserProfileSaverService) UserProfileSaver: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(AdvertiserResource) private readonly Advertiser: any,
    @Inject(PrintLineItemResource) private readonly PrintLineItem: any,
    @Inject(PublicPoliticalCampaignResource) private readonly PublicPoliticalCampaign: any,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    private readonly angularModalService: AngularModalService,
    ngZone: NgZone
  ) {
    super(eventBus, UserProfileSaver, LocalNetworkProfile, ngZone);
    const advCount = LocalNetworkInfo.obtain().getInfo('advertiserCount');
    this.showAdvertiserSearch = !_.isFinite(advCount) || advCount >= 50;
  }

  get electionTopics(): string[] {
    const electionId = _.get(this.editingModel, ['election', 'id']);
    return (this.advertiserBudgets && electionId && this.advertiserBudgets[electionId]) || [];
  }

  readonly compareById = compareById;

  protected afterInitEditingModel(): void {
    if (this.editingModel.startDate) {
      this.editingModel.modStartDate = this.editingModel.startDate;
    }
    if (this.editingModel.endDate) {
      this.editingModel.modEndDate = this.editingModel.endDate;
    }

    const selectedElectionId = _.get(this.editingModel, ['election', 'id']);
    this.elections = buildElectionFormOptions(LocalNetworkInfo.obtain().getSuppInfo('elections') || [], {
      username: this.localUserProfile.get('username'),
      canSeeTestElections: this.localUserProfile.canSeeTestElections(),
      selectedElectionId: selectedElectionId,
      onlyFuture: true
    });
    this.electionSelectGroups = buildElectionSelectGroups(this.elections);

    const selectedElection = _.find(this.elections, (election: any) => {
      return sameElectionId(election.id, selectedElectionId);
    });
    if (selectedElection) {
      this.editingModel.election = selectedElection;
    }

    if (advertiserNeedsPoliticalData(this.editingModel.advertiser)) {
      this.loadUpAdvertiser();
    } else {
      this.loadAdvertiserBudgets();
    }

    if (!this.showAdvertiserSearch) {
      this.Advertiser.query({minimal: true, pageSize: 1000}, (page: any) => {
        this.ngZone.run(() => {
          this.advertisers = page.results;
        });
      });
    }
  }

  protected afterInitAfterSave(): void {
    if (advertiserNeedsPoliticalData(this.editingModel.advertiser)) {
      this.loadUpAdvertiser();
    } else {
      this.loadAdvertiserBudgets();
    }
  }

  protected onBeforeSubmit(): void {
    this.editingModel.startDate = _.isDate(this.editingModel.modStartDate)
      ? this.editingModel.modStartDate
      : null;
    this.editingModel.endDate = _.isDate(this.editingModel.modEndDate)
      ? this.editingModel.modEndDate
      : null;
  }

  onAdvertiserChange(): void {
    this.selectObj.onSelect();
    this.markExternalFormDirty();
  }

  getQrCode(): void {
    const openModal = (qrSuccess: boolean, image?: string, eps?: string, link?: string) => {
      this.angularModalService.show(GetCampaignQrCodeModalComponent, {
        class: 'modal-sm',
        initialState: {
          qrSuccess,
          image: image || '',
          eps: eps || '',
          link: link || ''
        }
      });
    };

    this.PrintLineItem.getLink({id: this.editingModel.id, printAd: true}).$promise.then(
      (response: any) => {
        openModal(true, response.qrCode, response.qrCodeEps, response.link);
      },
      () => {
        openModal(false);
      }
    );
  }

  private loadUpAdvertiser(): void {
    this.Advertiser.get({id: _.get(this.editingModel, ['advertiser', 'id'])}, (advertiser: any) => {
      this.ngZone.run(() => {
        this.editingModel.advertiser = advertiser;
        this.applyAdvertiserBudgets(advertiser);
      });
    });
  }

  private loadAdvertiserBudgets(): void {
    const advertiser = this.editingModel.advertiser;
    if (!advertiser) {
      this.advertiserBudgets = null;
      this.advertiserBudgetsResolved = true;
      return;
    }

    if (advertiserNeedsPoliticalData(advertiser)) {
      this.loadUpAdvertiser();
      return;
    }

    this.applyAdvertiserBudgets(advertiser);
  }

  private applyAdvertiserBudgets(advertiser: any): void {
    const linkedCampaigns = linkedPoliticalCampaignRefs(advertiser);
    if (linkedCampaigns.length > 0) {
      this.advertiserBudgetsResolved = false;
      loadPoliticalCampaignTopicsByElection(linkedCampaigns, this.PublicPoliticalCampaign).then((campaignTopics: any) => {
        this.ngZone.run(() => {
          this.advertiserBudgets = campaignTopics;
          this.advertiserBudgetsResolved = true;
        });
      });
      return;
    }

    this.advertiserBudgets = buildTopicsFromPoliticalBudgets(advertiser);
    this.advertiserBudgetsResolved = true;
  }
}
